/**
 * Протокол бэкенд ↔ публикатор. §Ключ 3.
 *
 * Конверт лизинга ЗАКРЫТ (z.strictObject) и не содержит неподписанных дубликатов
 * подписанных значений. Верхнеуровневых channel_id, content_hash, nonce, expires_at,
 * day_key здесь нет и быть не может: естественная реализация прочитала бы именно их,
 * и вся подпись превратилась бы в украшение — сломанный бэкенд подменил бы канал
 * одной строкой JSON.
 *
 * Всё, что публикатору нужно для РЕШЕНИЯ, он берёт из проверенного payload токена
 * или из собственного конфига. Объект display существует только для строки лога и
 * разбирается ровно в одном файле — правило R10 стража границ.
 */

import * as z from 'zod';
import { UUID_V7_RE } from '../ids.ts';
import { DraftContentSchema } from '../telegram/draft.ts';
import { TelegramMessageSchema } from '../telegram/message.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);
const Token = z.string().max(8192);

/** Четыре поля вместо десяти. Токен — единственный авторитет. */
export const ClaimSchema = z.strictObject({
  lease_id: Uuid,
  key: Token,
  /** Транспортная форма; проверяется пересчётом хэша, а не доверием. */
  content: DraftContentSchema,
  media_id: Uuid.nullable(),
  /**
   * Адрес байтов документа (PDF внизу поста, 19.09.2026). Как и `media_id`, — адрес, а не
   * содержимое: дескриптор с sha256 лежит в подписанном `content.document`, и байты
   * сверяются с ним. Необязательно на проводе: бэкенд до 19.09.2026 поля не шлёт.
   */
  document_id: Uuid.nullable().default(null),
  /** ТОЛЬКО для логов. Имя обязано кричать: в diff'е ревью job.channel_id выглядит невинно. */
  display: z.strictObject({
    brand_display: z.string(),
    draft_id_display: z.string(),
    draft_version_display: z.int(),
    slot_start_display: IsoUtcMs,
    lease_expires_display: IsoUtcMs,
  }),
});
export type Claim = z.output<typeof ClaimSchema>;

/**
 * Отправка в ТЕСТОВЫЙ канал по ключу `preview` (docs/release-2026-09-19.md §9.1). Форма —
 * та же, что у публикации: токен единственный авторитет (канал, хэш, окно — в подписанном
 * payload), содержание проверяется пересчётом хэша. Отличий у публикатора три: канал обязан
 * быть тестовым каналом бренда, реестр свой (`preview_used`) и дневного лимита нет. Версия
 * черновика не меняется ни на одном шаге — превью не публикация.
 */
export const PreviewSchema = z.strictObject({
  /** Строка `test_send` бэкенда; равна `publication_id` подписанного payload. */
  preview_id: Uuid,
  key: Token,
  content: DraftContentSchema,
  media_id: Uuid.nullable(),
  document_id: Uuid.nullable(),
  /** ТОЛЬКО для логов — правило R10, как и у `Claim.display`. */
  display: z.strictObject({
    brand_display: z.string(),
    draft_id_display: z.string(),
    draft_version_display: z.int(),
  }),
});
export type Preview = z.output<typeof PreviewSchema>;

/** Работа по разрешению неопределённости (§Ключ 7.4). Токен целиком — чтобы chat_id
 *  и content_hash пробы тоже были подписанными. */
export const ResolutionSchema = z.strictObject({
  resolution_id: Uuid,
  key: Token,
  content: DraftContentSchema,
  last_known_message_id: z.int().positive().nullable(),
});
export type Resolution = z.output<typeof ResolutionSchema>;

/** Задание на отзыв. Ни chat_id, ни message_id верхним уровнем нет: оба подписаны. */
export const RetractionSchema = z.strictObject({
  retraction_id: Uuid,
  key: Token,
  /** ТОЛЬКО для лога, правило R10 — как и display. */
  last_message_id: z.int().positive(),
});
export type Retraction = z.output<typeof RetractionSchema>;

export const ClaimsResponseSchema = z.strictObject({
  claims: z.array(ClaimSchema).max(1),
  resolutions: z.array(ResolutionSchema),
  retractions: z.array(RetractionSchema).max(1),
  /**
   * Не больше одного превью за тик, ПОСЛЕ публикаций: отправка в тестовый канал не вправе
   * задержать пост, у которого наступил слот. Необязательно на проводе — бэкенд до
   * 19.09.2026 поля не шлёт.
   */
  previews: z.array(PreviewSchema).max(1).default([]),
});
export type ClaimsResponse = z.output<typeof ClaimsResponseSchema>;

/** Запись намерения ДО отправки. attempt_id генерирует публикатор — это снимает
 *  зависимость критического пути от доступности бэкенда. */
export const AttemptOpenSchema = z.strictObject({
  lease_id: Uuid,
  attempt_id: Uuid,
  nonce: z.string().regex(/^[0-9a-f]{32}$/),
  sending_at: IsoUtcMs,
});
export type AttemptOpen = z.output<typeof AttemptOpenSchema>;

export const FailureClassSchema = z.enum(['telegram_api', 'network', 'timeout', 'transport']);

const FailureSchema = z.strictObject({
  class: FailureClassSchema,
  code: z.string(),
  description: z.string(),
  /** Из parameters.retry_after при 429. Записывается ТОЛЬКО в журнал: повторять нечего. */
  retry_after_s: z.int().nullable(),
});

const DeviationSchema = z.looseObject({
  ok: z.boolean(),
  deviations: z.array(z.looseObject({})),
});

export const AttemptResultSchema = z
  .strictObject({
    outcome: z.enum(['sent', 'failed', 'uncertain']),
    sent: z
      .strictObject({
        message_id: z.int().positive(),
        date: z.int(),
        returned_message: TelegramMessageSchema,
        /** Обязательное поле ветки sent: отправки без сверки не бывает (§Ключ 5.1 шаг 31). */
        deviation: DeviationSchema,
      })
      .optional(),
    failure: FailureSchema.optional(),
    finished_at: IsoUtcMs,
  })
  .check((ctx) => {
    const v = ctx.value;
    if ((v.outcome === 'sent') !== (v.sent !== undefined)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['sent'],
        message: 'sent_required_for_sent_outcome',
      });
    }
    if ((v.outcome !== 'sent') !== (v.failure !== undefined)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['failure'],
        message: 'failure_required_for_non_sent',
      });
    }
  });
export type AttemptResult = z.output<typeof AttemptResultSchema>;

/**
 * Отказ ДО отправки (провал любой из проверок шагов 1–25).
 * Поля nonce_consumed здесь НЕТ: зеркало пишется только на POST /attempts, а этот маршрут
 * вызывается ровно тогда, когда до него дело не дошло. Отказ зеркала не порождает никогда.
 */
export const RefuseSchema = z.strictObject({
  reason: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/),
  detail: z.string().max(2048),
});
export type Refuse = z.output<typeof RefuseSchema>;

export const ResolutionResultSchema = z
  .strictObject({
    kind: z.enum(['found', 'not_found', 'needs_human']),
    message_id: z.int().positive().optional(),
    verified_by: z.literal('forward_probe').optional(),
    detail: z.string().max(2048).optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if ((v.kind === 'found') !== (v.message_id !== undefined)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['message_id'],
        message: 'message_id_required_for_found',
      });
    }
  });
export type ResolutionResult = z.output<typeof ResolutionResultSchema>;

export const RetractionResultSchema = z
  .strictObject({
    outcome: z.enum(['retracted', 'refused', 'failed']),
    reason: z.string().optional(),
    code: z.string().optional(),
    description: z.string().optional(),
    detail: z.string().optional(),
    finished_at: IsoUtcMs,
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.outcome === 'refused' && v.reason === undefined) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['reason'],
        message: 'reason_required_for_refused',
      });
    }
    if (v.outcome === 'failed' && v.code === undefined) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['code'],
        message: 'code_required_for_failed',
      });
    }
  });
export type RetractionResult = z.output<typeof RetractionResultSchema>;

/**
 * Исход превью — `POST /dispatch/v1/previews/:id/result`. Одна попытка, как у публикации, но
 * без разбора неопределённости: дубль в ТЕСТОВОМ канале безвреден, поэтому «ушло или нет —
 * неизвестно» здесь честно `failed`, а не отдельная ветка с пробой.
 *   • `sent` — Telegram принял; сверка обязательна, как у публикации;
 *   • `failed` — отправка не удалась (Bot API, сеть, таймаут);
 *   • `refused` — отказ ДО отправки: ключ не прошёл проверку, канал не тестовый, байты не те.
 */
export const PreviewResultSchema = z
  .strictObject({
    outcome: z.enum(['sent', 'failed', 'refused']),
    sent: z
      .strictObject({
        message_id: z.int().positive(),
        date: z.int(),
        returned_message: TelegramMessageSchema,
        deviation: DeviationSchema,
      })
      .optional(),
    failure: FailureSchema.optional(),
    refusal: RefuseSchema.optional(),
    finished_at: IsoUtcMs,
  })
  .check((ctx) => {
    const v = ctx.value;
    const want = {
      sent: v.outcome === 'sent',
      failed: v.outcome === 'failed',
      refused: v.outcome === 'refused',
    };
    if (want.sent !== (v.sent !== undefined)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['sent'],
        message: 'sent_required_for_sent_outcome',
      });
    }
    if (want.failed !== (v.failure !== undefined)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['failure'],
        message: 'failure_required_for_failed',
      });
    }
    if (want.refused !== (v.refusal !== undefined)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['refusal'],
        message: 'refusal_required_for_refused',
      });
    }
  });
export type PreviewResult = z.output<typeof PreviewResultSchema>;

export const HeartbeatSchema = z.strictObject({
  now_ms: z.int(),
  tick_seq: z.int().nonnegative(),
  ledger_ok: z.boolean(),
  journal_orphans: z.int().nonnegative(),
  /**
   * Момент, до которого публикатор обещает следующий тик, — ms UTC. Считает ОН из своего
   * `SMM_PUBLISHER_TICK_MS` (ПРАВИЛО C3-19, 06-security-ops §7.3): источник правды об
   * ожидании — продюсер, не конфиг бэкенда. Поле опционально, пока публикатор фазы 1 его
   * не шлёт: бэкенд тогда подставляет period+grace объявленного ожидания.
   */
  next_expected_at_ms: z.int().positive().optional(),
});
export type Heartbeat = z.output<typeof HeartbeatSchema>;
